import type { BulkAssignCoachesResponse } from './coachingDashboard.types';

export type NamedRef = Readonly<{ id: number; name: string }>;

export type BulkAssignResult =
  | Readonly<{ kind: 'full'; newCount: number; alreadyCount: number }>
  | Readonly<{
      kind: 'partial';
      newCount: number;
      alreadyCount: number;
      skippedCoaches: NamedRef[];
      skippedMembers: NamedRef[];
    }>
  | Readonly<{ kind: 'noop'; alreadyCount: number }>
  | Readonly<{
      kind: 'failed';
      skippedCoaches: NamedRef[];
      skippedMembers: NamedRef[];
    }>;

export type BulkAssignLookups = Readonly<{
  coachesById: ReadonlyMap<number, NamedRef>;
  membersById: ReadonlyMap<number, NamedRef>;
}>;

const toNamed = (ids: number[], lookup: ReadonlyMap<number, NamedRef>): NamedRef[] =>
  ids.map((id) => lookup.get(id) ?? { id, name: `#${id}` });

export const parseBulkAssignResult = (
  response: BulkAssignCoachesResponse,
  { coachesById, membersById }: BulkAssignLookups,
): BulkAssignResult => {
  const newCount = response.assignments.length;
  const alreadyCount = response.alreadyAssigned.length;
  const skippedCoaches = toNamed(response.skippedCoachIds, coachesById);
  const skippedMembers = toNamed(response.skippedUserIds, membersById);
  const anySkipped = skippedCoaches.length > 0 || skippedMembers.length > 0;

  if (newCount === 0 && !anySkipped) {
    return { kind: 'noop', alreadyCount };
  }

  if (newCount === 0) {
    return { kind: 'failed', skippedCoaches, skippedMembers };
  }

  if (!anySkipped) {
    return { kind: 'full', newCount, alreadyCount };
  }

  return { kind: 'partial', newCount, alreadyCount, skippedCoaches, skippedMembers };
};
